import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useNavigateParams } from '../../../hooks/useNavigateParams';

import { Context } from '../../../context/UserContext';
import Form from './Form';
import styles from '../../form/Form.module.css';

/* hooks */
import useFlashMessage from '../../../hooks/useFlashMessage';
import * as AlmoxarifadoAPI from '../../../api/almoxarifado';
import * as EmpresaAPI from '../../../api/empresa';
import * as TabelaAPI from '../../../api/tabela';
import * as ProdutoAPI from '../../../api/produto';

//boot
import { applyProdutoBoot } from './boot';

//helpers
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { buildMultipartWithJson } from '../../../helpers/httpForm';
import { assetsToFiles, dedupeFiles } from '../../../helpers/uploadHelpers';
import { resolveEmpresaTabela } from '../../../utils/funcoes';
import { resolveProdutoProfile, sanitizeProdutoByProfile } from './profiles/index';

const CreateOrEdit = () => {
    const [dados, setDados] = useState({});
    const token = localStorage.getItem('token') || ''; // ✅ fix
    const { parametro } = useContext(Context);
    const [tabEnabled, setTabEnabled] = useState({});
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const urlParams = new URLSearchParams(window.location.search);

    //escopo empresa
    const { empresaId, empresaIds } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);

    const pathname = '/Produtos';
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');
    const tipo = urlParams.get('tipo');
    const categoria = tipo;
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText');

    const profile = useMemo(() => resolveProdutoProfile({ categoria }), [categoria]);

    const params = new URLSearchParams({
        ul,
        li,
        tipo,
        categoria,
        operacao: operacaoUUID,
        uuid,
        btnText,
    });

    //enums, selects e datalist
    const [OptionsEmpresa, setEmpresa] = useState([]);
    const [OptionsUnidade, setUnidade] = useState([]);
    const [OptionsEmbalagem, setEmbalagem] = useState([]);
    const [OptionsEmbalagemProducao, setOptionsEmbalagemProducao] = useState([]);
    const [OptionsAlmoxarifados, setOptionsAlmoxarifado] = useState([]);
    const [OptionsCores, setOptionsCores] = useState([]);

    //set enums
    const [EnumTipo, setTipo] = useState([]);
    const [EnumOrigemCadastro, setOrigemCadastro] = useState([]);
    const [EnumBemTipo, setBemTipo] = useState([]);
    const [EnumPosicao, setEnumPosicao] = useState();

    //carregar ENUMS
    const consultaEnum = async (table, field, filter) => {
        const result = await TabelaAPI.enums(table, field, filter);

        if (table === 'Produtos') {
            // 1. Lógica específica para a tabela Produtos (com filtros por Profile)
            if (field === 'tipo') {
                const exclusoesPorProfile = {
                    produto: ['Imobilizado'],
                    imovel: ['Serviço', 'Uso Consumo'],
                    vtr: ['Serviço', 'Uso Consumo'],
                };

                const excluidos = exclusoesPorProfile[profile.id] || [];
                const filtrados = result.filter((item) => !excluidos.includes(item));

                setTipo(filtrados);
                return; // Finaliza aqui
            }
            if (field === 'origemCadastro') {
                setOrigemCadastro(result ? result : []);
            }
        } else if (table === 'produtoBens') {
            field === 'tipo' && setBemTipo(result ? result : []);
        } else if (table === 'produtoBensInventarioImagens') {
            field === 'posicao' && setEnumPosicao(result ? result : []);
        }
    };
    const consultaAlmoxarifados = async (empresaId) => {
        const result = await AlmoxarifadoAPI.almoxarifadosByEmpresaId(empresaId);
        setOptionsAlmoxarifado(result || []);
        return result;
    };
    const consultaEmpresa = async () => {
        const result = await EmpresaAPI.empresa(empresaId);
        setEmpresa(result);
    };
    const consultaUnidade = async () => {
        const result = await EmpresaAPI.unidade(empresaId, false, false, false, false);
        setUnidade(result);
    };

    //vários tipos de consulta
    const consultaTabela = async (empresaId, tabela, order = 'descricao') => {
        const result = await TabelaAPI.tabela(empresaId, tabela, order);
        if (tabela === 'Cor') {
            setOptionsCores(result);
        }
        if (tabela === 'Embalagem') {
            setEmbalagem(result);

            // para embalagens da indsutria
            const embalagensIndustria = (result || []).filter((item) => item?.industria === true);
            setOptionsEmbalagemProducao(embalagensIndustria);
        }
    };

    async function salvarProduto(payload) {
        let urlNavigate = pathname;

        // ---------------------------------------------
        // 1) arquivos "normais" do produto
        // ---------------------------------------------
        const files = {
            images: payload?.images ?? [],
            logomarca: payload?.logomarca ?? [],
            assinatura: payload?.assinatura ?? [],
            arquivo: payload?.arquivo ?? [],
        };

        // ---------------------------------------------
        // 2) anexos do inventário (produtoBensInventarios)
        // ---------------------------------------------
        const invs = payload?.produtoBens?.produtoBensInventarios ?? [];

        for (const inv of invs) {
            const itemTempId = inv?.tempId;
            if (!itemTempId) continue;

            // -----------------------------
            // IMAGENS do inventário
            // fontes aceitas:
            //  - inv.imagens: File[]
            //  - inv.draftImagens: Asset[]
            // -----------------------------
            const imgsA = Array.isArray(inv?.imagens) ? inv.imagens : [];
            const imgsB = Array.isArray(inv?.draftImagens) ? assetsToFiles(inv.draftImagens) : [];

            const imgFiles = dedupeFiles([...imgsA, ...imgsB]);
            if (imgFiles.length) {
                files[`inventarioImagens__${itemTempId}`] = imgFiles;
            }

            // -----------------------------
            // NFs (PDF) do inventário
            // fontes aceitas:
            //  - inv.nfs: File[]
            //  - inv.draftNfs: Asset[] (PADRÃO)
            // -----------------------------
            const nfsA = Array.isArray(inv?.nfs) ? inv.nfs : [];
            const nfsB = Array.isArray(inv?.draftNfs) ? assetsToFiles(inv.draftNfs) : [];

            const nfFiles = dedupeFiles([...nfsA, ...nfsB]);
            if (nfFiles.length) {
                files[`inventarioNfs__${itemTempId}`] = nfFiles;
            }
        }

        // ---------------------------------------------
        // 3) remove arquivos do JSON (evita file virar {})
        // ---------------------------------------------
        const { images, logomarca, assinatura, arquivo, ...dataSemFilesRaw } = payload;

        // ✅ evita mutação do payload/state
        const dataSemFiles = { ...dataSemFilesRaw };

        if (dataSemFiles?.produtoBens?.produtoBensInventarios?.length) {
            const pb = { ...dataSemFiles.produtoBens };

            pb.produtoBensInventarios = pb.produtoBensInventarios.map((inv) => {
                const draftImagens = Array.isArray(inv?.draftImagens)
                    ? inv.draftImagens.map(({ file, ...rest }) => rest)
                    : inv?.draftImagens;

                const draftNfs = Array.isArray(inv?.draftNfs) ? inv.draftNfs.map(({ file, ...rest }) => rest) : inv?.draftNfs;

                // zera arrays que poderiam conter File/Blob (arquivos vão no multipart)
                const imagens = Array.isArray(inv?.imagens) ? [] : inv?.imagens;
                const nfs = Array.isArray(inv?.nfs) ? [] : inv?.nfs;

                return {
                    ...inv,
                    imagens,
                    nfs,
                    draftImagens,
                    draftNfs,
                };
            });

            dataSemFiles.produtoBens = pb;
        }

        // ---------------------------------------------
        // 4) cria multipart (json + files)
        // ---------------------------------------------
        const fd = buildMultipartWithJson({ data: dataSemFiles, files });

        try {
            let response = '';
            let urlNavigate = pathname; // valor padrão

            if (uuid && uuid !== 'null') {
                response = await ProdutoAPI.editar(fd);
            } else {
                response = await ProdutoAPI.cadastrar(fd);

                if (response?.status === 202) {
                    const { data } = response;
                    setDados(data);
                    response.data.message = 'Registro em duplicidade!';
                    response.data.type = 'warning';
                    urlNavigate = pathname + '/CreateOrEdit';
                    params.set('uuid', data.uuid);
                    params.set('btnText', 'Atualizar');
                    params.set('duplicidade', true);
                }
            }

            // Fluxo de Sucesso (200, 201, 202)
            setFlashMessage(response?.data?.message, response?.data?.type);

            setTimeout(() => {
                navigate(urlNavigate, params);
            }, parametro.msgTempo * 1000);
        } catch (error) {
            // Fluxo de Erro (422, 400, 500)
            if (error.response) {
                const { data } = error.response;

                // Exibe a mensagem vinda do backend ("O fabricante é obrigatório...")
                setFlashMessage(data.message, data.type || 'error');

                // Se quiser que o 422 também redirecione após o erro, mantenha o setTimeout aqui:
                /* 
                    setTimeout(() => {
                        navigate(urlNavigate, params);
                    }, parametro.msgTempo * 1000); 
                */
            } else {
                console.error('Erro na requisição:', error.message);
            }
        }
    }
    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        //popula selects
        consultaTabela(empresaId, 'Cor');
        consultaTabela(empresaId, 'Embalagem');
        consultaEmpresa();
        consultaUnidade();
        consultaAlmoxarifados(empresaIds);

        //enums
        consultaEnum('Produtos', 'tipo');
        consultaEnum('Produtos', 'origemCadastro');
        consultaEnum('produtoBens', 'tipo');
        consultaEnum('produtoBensInventarioImagens', 'posicao');

        setTabEnabled({
            produto: profile.tabs.cabecalho?.enabled,
            industria: profile.tabs.industria?.enabled,
            materiaPrima: profile.tabs.materiaPrima?.enabled,
            modoProducao: profile.tabs.modoProducao?.enabled,
            embalagens: profile.tabs.embalagens?.enabled,
            caracteristicas: profile.tabs.caracteristicas?.enabled,
            bens: profile.tabs.bens?.enabled,
            mobilia: profile.tabs.mobilia?.enabled,
            imagens: profile.tabs.imagens?.enabled,
        });

        async function carregar() {
            try {
                if (!uuid || uuid === 'null') {
                    let base = {};
                    base = applyProdutoBoot({ prev: base, categoria, parametro });
                    setDados(base);
                    return;
                }

                ProdutoAPI.consulta(uuid)
                    .then((response) => {
                        setDados(response.data);
                    })
                    .catch((error) => {
                        console.error(error);
                        setFlashMessage(
                            error.response.data.message || 'Erro carregar lista',
                            error.response.data.type || 'error'
                        );
                    });
                return;
                const resp = await ProdutoAPI.obterProdutoByIdOrUUID({ uuid });

                if (!resp) return;
                const raw = resp.data;
                const clean = sanitizeProdutoByProfile({ raw, profile });
                setDados(clean);
            } catch (error) {
                console.error(error);
                setFlashMessage(error.response?.data?.message || error.message, error.response?.data?.type || 'error');
            }
        }

        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, uuid, categoria, profile]); // ✅ include profile

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                token={token}
                handleSubmit={salvarProduto}
                formData={dados}
                btnText={btnText}
                //enums
                EnumBemTipo={EnumBemTipo}
                EnumTipo={EnumTipo}
                EnumOrigemCadastro={EnumOrigemCadastro}
                EnumPosicao={EnumPosicao}
                //options
                OptionsAlmoxarifados={OptionsAlmoxarifados}
                OptionsCores={OptionsCores}
                OptionsEmbalagem={OptionsEmbalagem}
                OptionsEmbalagemProducao={OptionsEmbalagemProducao}
                OptionsEmpresa={OptionsEmpresa}
                OptionsUnidade={OptionsUnidade}
                caminhoVoltar={pathname}
                empresaId={empresaId}
                empresaIds={empresaIds}
                empresaTabelaId={empresaTabelaId}
                profile={profile}
                tabEnabled={tabEnabled} // ✅ passa para o Form se ele usa
            />
        </section>
    );
};

export default CreateOrEdit;
